import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Truck, Calendar, DollarSign, ArrowLeft, CheckCircle2, XCircle, Clock, Mail, Phone } from 'lucide-react';
import { formatDateAU } from '../utils/dateFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  requested: { label: 'Waiting on your rate', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  rate_offered: { label: 'Rate sent — awaiting hirer', cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  awaiting_insurance: { label: 'Hirer uploading insurance', cls: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
  insurance_pending_review: { label: 'Admin reviewing insurance', cls: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
  ready_for_payment: { label: 'Awaiting hirer payment', cls: 'bg-purple-100 text-purple-800 border-purple-300' },
  payment_pending: { label: 'Payment in progress', cls: 'bg-purple-100 text-purple-800 border-purple-300' },
  paid: { label: 'Paid ✓', cls: 'bg-green-100 text-green-800 border-green-300' },
  declined: { label: 'Declined', cls: 'bg-gray-200 text-gray-600 border-gray-300' },
  cancelled: { label: 'Cancelled', cls: 'bg-gray-200 text-gray-600 border-gray-300' }
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || { label: status, cls: 'bg-gray-100 text-gray-700 border-gray-300' };
  return <span className={`px-2 py-0.5 text-xs rounded-full border font-semibold ${m.cls}`}>{m.label}</span>;
};

const CarrierTrailerHireBookings = () => {
  const navigate = useNavigate();
  const carrierEmail = localStorage.getItem('user_email');
  const [bookings, setBookings] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offerFor, setOfferFor] = useState(null); // booking currently being quoted
  const [rate, setRate] = useState({ amount: '', period: 'day', notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!carrierEmail) {
      navigate('/login');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [b, l] = await Promise.all([
          axios.get(`${API}/trailer-hire/bookings/operator/${encodeURIComponent(carrierEmail)}`),
          axios.get(`${API}/trailer-hire/my-listings/${encodeURIComponent(carrierEmail)}`)
        ]);
        if (!cancelled) {
          setBookings(b.data || []);
          setListings(l.data || []);
        }
      } catch {
        if (!cancelled) toast.error('Failed to load bookings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [carrierEmail, navigate]);

  const listingLookup = Object.fromEntries(listings.map(l => [l.id, l]));

  const submitRate = async () => {
    if (!rate.amount || !rate.period) { toast.error('Rate + period required'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/trailer-hire/bookings/${offerFor.id}/offer-rate`, {
        operator_email: carrierEmail,
        amount: parseFloat(rate.amount),
        period: rate.period,
        notes: rate.notes || null
      });
      toast.success('Rate sent to hirer');
      setOfferFor(null);
      setRate({ amount: '', period: 'day', notes: '' });
      // Refresh
      const b = await axios.get(`${API}/trailer-hire/bookings/operator/${encodeURIComponent(carrierEmail)}`);
      setBookings(b.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send rate');
    } finally { setSaving(false); }
  };

  const decline = async (booking) => {
    if (!window.confirm(`Decline the booking request from ${booking.hirer_name}?`)) return;
    try {
      await axios.put(`${API}/trailer-hire/bookings/${booking.id}/decline`, {
        operator_email: carrierEmail
      });
      toast.success('Request declined');
      setBookings(prev => prev.map(x => x.id === booking.id ? { ...x, status: 'declined' } : x));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to decline');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="bg-[#1e3a5f] text-white py-6">
        <div className="container mx-auto px-4 flex items-center justify-between max-w-5xl">
          <div className="flex items-center gap-3">
            <Truck className="w-7 h-7 text-red-300" />
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">Truck &amp; Trailer Hire — Booking Requests</h1>
              <p className="text-xs text-blue-200">Quote or decline incoming hire requests for your listed equipment.</p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate('/carrier-dashboard')}
            className="bg-white/10 border-white/40 text-white hover:bg-white hover:text-[#1e3a5f]"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-5xl">
        {loading ? (
          <Card className="p-12 text-center text-gray-500">Loading…</Card>
        ) : bookings.length === 0 ? (
          <Card className="p-12 text-center text-gray-500" data-testid="th-empty">
            <Truck className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p>No booking requests yet.</p>
            {listings.length === 0 && (
              <Button
                onClick={() => navigate('/list-trailer')}
                className="mt-4 bg-[#1e3a5f] hover:bg-[#15304f] text-white"
              >
                List a truck or trailer first
              </Button>
            )}
          </Card>
        ) : (
          <div className="space-y-3">
            {bookings.map(b => {
              const listing = listingLookup[b.listing_id];
              const listingTitle = listing
                ? (listing.vehicle_category === 'truck'
                    ? (listing.truck_type === 'Other' ? listing.truck_type_other : listing.truck_type) || 'Truck'
                    : listing.trailer_type || 'Trailer')
                : 'Listing';
              return (
                <Card key={b.id} className="p-4" data-testid={`booking-card-${b.id}`}>
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-3 mb-3">
                    <div className="flex-1">
                      <div className="flex items-center flex-wrap gap-2 mb-1">
                        <h3 className="font-bold text-[#1e3a5f]">{listingTitle}</h3>
                        <StatusBadge status={b.status} />
                      </div>
                      <p className="text-sm text-gray-700 mb-1 flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5 text-gray-400" />
                        {b.start_date} → {b.end_date}
                      </p>
                      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-gray-700">
                        <div><strong>{b.hirer_name}</strong>{b.hirer_company_name ? ` — ${b.hirer_company_name}` : ''}</div>
                        <div className="flex items-center gap-1"><Mail className="w-3.5 h-3.5 text-gray-400" /><a href={`mailto:${b.hirer_email}`} className="underline">{b.hirer_email}</a></div>
                        <div className="flex items-center gap-1"><Phone className="w-3.5 h-3.5 text-gray-400" /><a href={`tel:${b.hirer_phone}`} className="underline">{b.hirer_phone}</a></div>
                        {b.operator_rate_amount && (
                          <div className="flex items-center gap-1"><DollarSign className="w-3.5 h-3.5 text-gray-400" />Your rate: <strong>${b.operator_rate_amount} / {b.operator_rate_period}</strong></div>
                        )}
                        <div className="flex items-center gap-1 text-xs text-gray-500 sm:col-span-2"><Clock className="w-3 h-3" />Requested {formatDateAU(b.created_at)}</div>
                        {b.notes && <div className="sm:col-span-2 italic text-gray-600 mt-1">Note from hirer: {b.notes}</div>}
                      </div>
                    </div>
                  </div>

                  {/* Action row */}
                  {b.status === 'requested' && (
                    <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100">
                      <Button
                        size="sm"
                        className="bg-red-600 hover:bg-red-700 text-white"
                        onClick={() => setOfferFor(b)}
                        data-testid={`offer-rate-${b.id}`}
                      >
                        <DollarSign className="w-3.5 h-3.5 mr-1" /> Offer rate
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 border-red-300 hover:bg-red-50"
                        onClick={() => decline(b)}
                        data-testid={`decline-${b.id}`}
                      >
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Decline
                      </Button>
                    </div>
                  )}
                  {b.status === 'paid' && (
                    <div className="pt-3 border-t border-gray-100 text-sm text-green-700 flex items-center gap-1">
                      <CheckCircle2 className="w-4 h-4" /> Paid. Truckn Send It will be in touch about payout.
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Rate offer modal */}
      {offerFor && (
        <div
          className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4"
          onClick={() => setOfferFor(null)}
          data-testid="offer-rate-modal"
        >
          <Card className="max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-[#1e3a5f] mb-2">Quote a rate</h3>
            <p className="text-sm text-gray-600 mb-4">
              For <strong>{offerFor.hirer_name}</strong> — {offerFor.start_date} → {offerFor.end_date}
            </p>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <Label>Amount (AUD) *</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={rate.amount}
                    onChange={(e) => setRate(p => ({ ...p, amount: e.target.value }))}
                    placeholder="e.g. 250"
                    data-testid="rate-amount"
                  />
                </div>
                <div>
                  <Label>Per *</Label>
                  <select
                    value={rate.period}
                    onChange={(e) => setRate(p => ({ ...p, period: e.target.value }))}
                    className="w-full h-10 px-2 mt-1 text-sm border border-gray-300 rounded-md"
                    data-testid="rate-period"
                  >
                    <option value="day">day</option>
                    <option value="week">week</option>
                    <option value="month">month</option>
                  </select>
                </div>
              </div>
              <div>
                <Label>Note (optional)</Label>
                <Input
                  value={rate.notes}
                  onChange={(e) => setRate(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Anything the hirer should know"
                  data-testid="rate-notes"
                />
              </div>
              <p className="text-xs text-gray-500">
                The hirer will see this rate + a 5% platform fee + card processing added on top when they pay.
              </p>
            </div>
            <div className="flex gap-2 mt-5">
              <Button
                variant="outline"
                onClick={() => setOfferFor(null)}
                className="flex-1"
                data-testid="rate-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={submitRate}
                disabled={saving}
                className="flex-1 bg-red-600 hover:bg-red-700"
                data-testid="rate-submit"
              >
                {saving ? 'Sending…' : 'Send rate'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default CarrierTrailerHireBookings;
